import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';

import { PageContainer } from '@src/shared/components/page-container';

import { InboxSystemBar } from './inbox-system-bar';
import { OmniSideMenu } from './omni-side-menu';

/**
 * Shared chrome for every omni-rail destination: system bar + rail + the
 * page for the active tab. Tab pages render through `<Outlet />` — they do
 * not pick each other.
 */
export function ConversationsLayout() {
  // Pointer cursor on every button / icon-button while this shell is open —
  // including Radix dialogs that portal to `document.body`.
  useEffect(() => {
    document.documentElement.setAttribute('data-conversations-page', '');
    return () => {
      document.documentElement.removeAttribute('data-conversations-page');
    };
  }, []);

  return (
    /* This shell does not scroll — its panes do, each on its own.
     *
     * `PageContainer`'s default is `min-h-[100dvh]`: at least a full viewport,
     * grow past it if the content wants to. That is right for a page you read
     * top to bottom and wrong for an inbox, where growing means the thread
     * list and the captured leads slide away with the transcript somebody is
     * reading. `flex-1 min-h-0` (tailwind-merge drops the default) pins the
     * page to exactly the region the layout gives it, and `overflow-hidden`
     * makes exceeding that a containment bug here rather than a scrollbar
     * around everything.
     *
     * From here down every pane is `min-h-0 flex-1` around an `overflow-y-auto`
     * child, which is what makes each one scroll alone. */
    <PageContainer
      data-slot='conversations-page'
      // Plain `min-h-0` (not `min-h-0!`): twMerge drops PageContainer's default
      // `min-h-[100dvh]` only when the conflicting class is in the same group.
      // A full-viewport min-height under the app header is taller than the pane,
      // and that extra height is what lets the document scroll the Conversations
      // chrome up.
      className='h-full max-h-full min-h-0 flex-1 gap-0 overflow-hidden p-0 [&_button]:cursor-pointer [&_[role=button]]:cursor-pointer [&_button:disabled]:cursor-not-allowed'
    >
      <div className='flex min-h-0 flex-1 flex-col overflow-hidden bg-white'>
        <InboxSystemBar />
        <div className='flex min-h-0 flex-1 overflow-hidden'>
          <OmniSideMenu />
          <Outlet />
        </div>
      </div>
    </PageContainer>
  );
}
